import { useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0DatePicker, F0Dialog } from '@factorialco/f0-react'
import { DatePickerValue, Input } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment-timezone'

import i18n from 'modules/core/lib/i18n'

interface ScheduleMessageModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (scheduledDate: string) => void
  initialDate?: Date
  initialTime?: string
}

const getRoundedTimeToNextHalfHour = (): { date: Date; time: string } => {
  const now = moment()
  const minutes = now.minutes()

  let roundedMoment: moment.Moment
  if (minutes === 0 || minutes === 30) {
    roundedMoment = now.clone().add(30, 'minutes').startOf('minute')
  } else if (minutes < 30) {
    roundedMoment = now.clone().minutes(30).seconds(0).milliseconds(0)
  } else {
    roundedMoment = now.clone().add(1, 'hour').startOf('hour')
  }

  return {
    date: roundedMoment.toDate(),
    time: roundedMoment.format('HH:mm'),
  }
}

export const ScheduleMessageModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialDate,
  initialTime,
}: ScheduleMessageModalProps) => {
  const userTimezone = moment.tz.guess()
  const timezoneAbbr = useMemo(() => {
    return moment.tz(userTimezone).format('z')
  }, [userTimezone])

  const roundedDefault = useMemo(() => getRoundedTimeToNextHalfHour(), [])
  const defaultDate = initialDate || roundedDefault.date
  const defaultTime = initialTime || roundedDefault.time

  const [selectedDate, setSelectedDate] = useState<Date>(defaultDate)
  const [selectedTime, setSelectedTime] = useState<string>(defaultTime)
  const [timeTouched, setTimeTouched] = useState(false)

  const handleDateChange = (
    date: DatePickerValue | undefined,
    _stringValue?: string | undefined
  ) => {
    if (date?.value?.from) {
      setSelectedDate(date.value.from)
    }
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
  }

  const handleTimeBlur = () => {
    setTimeTouched(true)
  }

  const getScheduledMoment = () => {
    const [hours, minutes] = selectedTime.split(':')
    if (!hours || !minutes) return null

    return moment(selectedDate)
      .set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10), seconds: 0 })
      .tz(userTimezone)
  }

  const isTimeInPast = useMemo(() => {
    const [hours, minutes] = selectedTime.split(':')
    if (!hours || !minutes) return false

    const scheduledMoment = moment(selectedDate)
      .set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10), seconds: 0 })
      .tz(userTimezone)

    return scheduledMoment.isBefore(moment())
  }, [selectedDate, selectedTime, userTimezone])

  const showTimeError = timeTouched && isTimeInPast

  const handleConfirm = () => {
    const scheduledMoment = getScheduledMoment()
    if (!scheduledMoment || isTimeInPast) return

    onConfirm(scheduledMoment.toISOString())
    onClose()
  }

  const datePickerValue = useMemo(
    () => ({
      granularity: 'day' as const,
      value: {
        from: selectedDate,
        to: selectedDate,
      },
    }),
    [selectedDate]
  )

  return (
    <F0Dialog
      isOpen={isOpen}
      width="sm"
      onClose={onClose}
      title={i18n.t('ats.candidates.messages.schedule_message_modal.title')}
      primaryAction={{
        label: i18n.t('ats.candidates.messages.schedule_message_modal.confirm'),
        onClick: handleConfirm,
        disabled: isTimeInPast,
      }}
      secondaryAction={{
        label: i18n.t('ats.candidates.messages.schedule_message_modal.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16">
        <Box gap="s16" flexDirection="row" width="full">
          <Box style={{ flex: '1' }}>
            <F0DatePicker
              label={i18n.t('ats.candidates.messages.schedule_message_modal.date')}
              onChange={handleDateChange}
              size="sm"
              value={datePickerValue}
              minDate={new Date()}
            />
          </Box>

          <Box flexDirection="row" alignItems="flexEnd" gap="s8" style={{ flex: '1' }}>
            <Box style={{ flex: '1' }} onBlur={handleTimeBlur}>
              <Input
                label={i18n.t('ats.candidates.messages.schedule_message_modal.time')}
                type="time"
                value={selectedTime}
                onChange={handleTimeChange}
                error={showTimeError}
              />
            </Box>
            <Box paddingBottom="s8" style={{ minWidth: '50px' }}>
              <Text.Normal size="200" color="grey600" weight="medium">
                {timezoneAbbr}
              </Text.Normal>
            </Box>
          </Box>
        </Box>
        {showTimeError && (
          <Text.Normal size="100" color="radicalRed1000">
            {i18n.t('ats.candidates.messages.schedule_message_modal.time_in_past_error')}
          </Text.Normal>
        )}
      </Box>
    </F0Dialog>
  )
}
